// What the identity vendor's card wears, computed at mount rather than written once as
// hexes.
//
// `SignIn.tsx` and `Invite.tsx` mount Clerk's `mountSignIn`/`mountSignUp` components, and
// both take an `appearance` option. `variables` is how the whole card's palette, focus
// ring, typefaces and corner radius get set in one pass — Clerk derives its own shades
// from a handful of base colors, which is far less work than restyling its DOM rule by
// rule. This module builds that object by reading the app's own custom properties off
// `document.documentElement` with `getComputedStyle`, the same tokens every other
// component in this package already reads through CSS. `docs/design-system.md` says a
// color belongs in the token table first and `styles.css` second; a hex typed into this
// file would be a third copy, and a silent one — nothing would catch it drifting from the
// table the next time a token's value changed, and it would be flatly wrong in whichever
// of the two themes it was not copied from.
//
// THE MAPPING is copied from the app's own tonal RELATIONSHIPS, not from tokens matched
// by name — the first pass here got that wrong in two places (see below), because copying
// the token that sounds right is easy to do without checking what actually sits next to
// what. `--accent` (Sage) is the app's one everyday interactive color, so it drives
// `colorPrimary` and, by the same logic, the focus ring (`colorRing`) — this package's
// other focus rings are already accent-derived (see the icon button in
// `docs/design-system.md`). `--accent-ink` is the text Sage is designed to sit under, so
// it is `colorPrimaryForeground`. `--card` is this app's own card surface, so it is
// `colorBackground` — the vendor's card should look like every other card in the product.
// `--ink` is body text, so it is both `colorForeground` and `colorInputForeground`.
// `--ink-soft` is this app's muted-text token, so it is `colorMutedForeground`. `--line`
// is every border in the app, so it is `colorBorder`. `--radius` is `borderRadius`
// outright. The sans stack (`fontFamily`, and `fontFamilyButtons` too: `docs/design-
// system.md` is explicit that headlines are the only place the serif belongs, so the
// card's body copy and its buttons both take the sans) is read straight off
// `document.body`'s computed style rather than typed out a second time here, for the
// same drift reason as the palette.
//
// `colorInput` and `colorMuted` are NOT `--card`, even though the card itself is
// `--card`, and this is the relationship the first pass missed for `colorInput`. Look at
// how the app's own controls actually work: `.newPlan__field` and `.button` are
// `background: var(--card)` with `border: 1px solid var(--line)`, and they sit on a
// `var(--paper)` page — that is two tonal steps, paper then a lighter card fill then a
// line edge. Inside the vendor's card, the card itself already occupies the `--card`
// step, so a control recessed into it needs the step below, `--paper`, to read with the
// same separation — otherwise an input has the exact same fill as the card around it and
// reads as edgeless. Both `colorInput` and `colorMuted` get `--paper` for that reason.
//
// `colorNeutral` is also new here, and it is a different kind of slot from the ones
// above: Clerk's docs describe it as the color "used to generate the neutral shades the
// components use" — dividers, the secondary/social button borders, muted surfaces — so
// it is not a background or foreground itself, it is what Clerk's own shade generator
// derives a whole ramp from. Left unset, Clerk falls back to its own default neutral,
// which is why the social buttons had no visible edge at all against a dark card. It gets
// `--ink`, and that is deliberate: this is the one slot here that is handed a
// foreground-ish token on purpose, because the ramp Clerk derives from it needs to lean
// dark-neutral to read against this card, not toward a background token. Do not "fix"
// this to `--card` or `--paper` later on the assumption every color slot wants a
// background — this one is a generator input, not a surface.
//
// `colorNeutral` above is kept, but it turned out not to be the fix for the social
// buttons: set to `--ink` or to any other neutral value, Clerk still renders those buttons
// with no border at all — the shade generator simply does not put one there. Nor was
// `elements` the fix. A pass that set `socialButtonsBlockButton` and `formFieldInput`
// through `elements` with an explicit `backgroundColor`, `borderColor`, `borderWidth` and
// `borderStyle` got the fill and lost every border property: Clerk's own stylesheet selects
// those buttons through their `data-variant="outline"` attribute and out-specifies a
// descriptor style object on exactly the properties that mattered. Both attempts were the
// same mistake in two costumes — trying to win a cascade fight inside the vendor's own
// stylesheet, on the vendor's terms.
//
// SO THE CONTROLS ARE STYLED FROM `styles.css` INSTEAD, and `cssLayerName` below is what
// makes that unloseable rather than merely one more attempt at being more specific. It is a
// documented top-level `appearance` key — a sibling of `variables` and `elements`, not a
// member of either — and it puts everything the vendor injects into a named cascade layer.
// Unlayered author CSS beats any `@layer` outright: the layer boundary is resolved before
// specificity is consulted at all, so a plain two-class rule in `styles.css` wins against
// anything the vendor can write, and keeps winning when the vendor ships a build whose
// internal selectors are shaped differently. That is the whole reason this pass holds and
// the two above did not. The rules themselves, the tokens they use, and why `--ink-faint`
// rather than `--line` draws the edge, live in the signin section of `styles.css`; this
// file's job for those two controls is now only to hand the vendor a layer name. Verified
// against the served build rather than the installed types (which lag it, see below):
// `cssLayerName` is read off `options.appearance` in `@clerk/ui`'s browser bundle, which is
// the script that actually renders these components.
//
// The `elements` entries for `socialButtonsBlockButton` and `formFieldInput` are gone
// rather than kept alongside those rules. Keeping the `backgroundColor` and `color` that
// did work would leave two mechanisms owning one control: either the vendor injects them
// inside the layer, where the CSS overrides them and they are dead weight, or it injects
// them unlayered, where they compete with the fix on specificity — which is the failure
// mode this change exists to end.
//
// Slots with no real token behind them — `colorDanger`, `fontFamilyMono`, `spacing`, and
// the rest of Clerk's documented list — are left out rather than filled with an invented
// value.
//
// NO REMOUNT ON THEME CHANGE. Clerk fixes `appearance` at mount time; it does not react
// to the card being handed a new palette later. That would matter if the app's theme
// could change while a signed-out screen was on screen, but it cannot: the only theme
// control lives in `AccountMenu.tsx`, which only renders once there is a session, and
// there is no session on `/signup`, `/signin` or `/invite`. The one gap is the operating
// system itself flipping `prefers-color-scheme` while somebody happens to be sitting on a
// signed-out screen — real, and not worth machinery for. Do not add a listener that remounts
// the card for that case, and do not delete this function later for looking unused in
// that scenario; it is read once per mount, which is what it is for.
//
// WHY NONE OF THIS CAN BE VALIDATED BY THE TYPE CHECKER OR A TEST. `appearance` is typed
// `any` end to end in the installed packages — `@clerk/shared`'s `Appearance` is a bare
// `any`, and the theme type it could otherwise narrow against resolves through an empty
// global registry interface. A misspelled variable or element name here is not a compile
// error and not a runtime error; it is silently ignored by the vendor and the card just
// keeps whatever it would have shown anyway. On top of that, the installed types
// (`@clerk/clerk-js` pinned to `^6.27.1`) describe an older build than the one that is
// actually served — the vendor ships its own scripts from the instance's frontend-API
// domain (see `clerk.ts`), and `window.Clerk.version` on a running dev build reads
// `6.30.1`. So this file's correctness is not something `tsc` or `vitest` can confirm;
// it is confirmed by looking at both cards in a browser, signed out, in both themes.

/**
 * Reads one of this app's custom properties off the live document, trimmed. `undefined`
 * when there is no document to read from, or when the property does not resolve to
 * anything — either way, the caller leaves the corresponding Clerk variable unset rather
 * than handing the vendor an empty string, which Clerk would not treat as "use your own
 * default" but as a color of nothing.
 */
function readToken(name: string): string | undefined {
  const root = globalThis.document?.documentElement;
  const value = root ? getComputedStyle(root).getPropertyValue(name).trim() : '';
  return value === '' ? undefined : value;
}

/** Same shape of read, for the sans stack declared on `body` rather than a custom property. */
function readSans(): string | undefined {
  const body = globalThis.document?.body;
  const value = body ? getComputedStyle(body).fontFamily.trim() : '';
  return value === '' ? undefined : value;
}

export function vendorAppearance(): Record<string, unknown> {
  const card = readToken('--card');
  const ink = readToken('--ink');
  const inkSoft = readToken('--ink-soft');
  const line = readToken('--line');
  const accent = readToken('--accent');
  const accentInk = readToken('--accent-ink');
  const paper = readToken('--paper');
  const radius = readToken('--radius');
  const sans = readSans();

  const variables: Record<string, string> = {};
  const set = (name: string, value: string | undefined): void => {
    if (value !== undefined) {
      variables[name] = value;
    }
  };

  set('colorPrimary', accent);
  set('colorPrimaryForeground', accentInk);
  set('colorBackground', card);
  set('colorForeground', ink);
  set('colorMutedForeground', inkSoft);
  set('colorMuted', paper);
  set('colorInput', paper);
  set('colorInputForeground', ink);
  set('colorBorder', line);
  set('colorRing', accent);
  set('colorNeutral', ink);
  set('borderRadius', radius);
  set('fontFamily', sans);
  set('fontFamilyButtons', sans);

  return {
    variables,
    // Every style the vendor injects goes into a cascade layer named `clerk`, which is
    // what lets the plain rules in `styles.css` dress the social buttons and the email
    // input without having to out-specify anything — see the long note above on why the
    // two earlier passes lost and this one cannot. The name is arbitrary and appears
    // nowhere else; nothing in `styles.css` declares a `@layer` of its own, and it must
    // not start doing so for these rules — the moment they are inside any layer they are
    // back to competing with the vendor on specificity, which is the bug.
    cssLayerName: 'clerk',
    // Our screens now own every heading and every word of voice on both signed-out
    // screens (see `docs/design-system.md`), so the vendor's own heading and subtitle
    // must not render. `header`, `headerTitle` and `headerSubtitle` are the three
    // descriptor names documented for this; which one the served build actually honours
    // was not worth pinning down by inspection when setting all three costs nothing; an
    // unrecognised name is silently ignored (see above), so this is belt-and-braces
    // rather than uncertainty left in the code.
    //
    // The vendor's "Last used" badge is a different job — it is kept, not hidden, and
    // restyled as a pill from `styles.css` rather than from here (search
    // `cl-lastAuthenticationStrategyBadge`). Three entries used to sit below this trio
    // trying to hide it by guessing at its descriptor name
    // (`socialButtonsBlockButtonBadge`, `socialButtonsProviderLastUsed`, `badge`); every
    // one was wrong, and because an unrecognised name is silently ignored, they read as a
    // fix while the badge went on rendering unstyled. The real descriptor is
    // `lastAuthenticationStrategyBadge`, read out of the served `@clerk/ui` bundle, and
    // `styles.css` uses it to style the badge directly through the same cascade-layer
    // mechanism that dresses the controls, rather than adding a fourth guess here.
    elements: {
      header: { display: 'none' },
      headerTitle: { display: 'none' },
      headerSubtitle: { display: 'none' },
    },
  };
}
